import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { API, useAuth, useSelectedDate, getWeeksInMonth } from "../App";
import { Users, TrendingUp, Target, Award, ChevronRight, ArrowUpDown, ArrowDown, ArrowUp, Trophy, Medal, Building2, DollarSign, CalendarDays } from "lucide-react";
import { useNavigate } from "react-router-dom";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from "recharts";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";

const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December"
];

// Department configurations with unique colors
const DEPARTMENTS = {
  sales: { label: "Sales", color: "#6366F1", bgColor: "bg-indigo-600" },
  csm: { label: "CSM", color: "#10B981", bgColor: "bg-emerald-600" },
  dev: { label: "Dev", color: "#8B5CF6", bgColor: "bg-purple-600" },
  support: { label: "Support", color: "#F59E0B", bgColor: "bg-amber-600" },
  marketing: { label: "Marketing", color: "#EC4899", bgColor: "bg-pink-600" },
};

const Dashboard = () => {
  const { token } = useAuth();
  const { selectedMonth, setSelectedMonth, selectedYear, setSelectedYear, viewMode, setViewMode, selectedWeek, setSelectedWeek } = useSelectedDate();
  const [teamStats, setTeamStats] = useState(null);
  const [salespeople, setSalespeople] = useState([]);
  const [selectedDepartment, setSelectedDepartment] = useState("all");
  const [loading, setLoading] = useState(true);
  const [sortColumn, setSortColumn] = useState("total");
  const [sortDirection, setSortDirection] = useState("desc");
  const navigate = useNavigate();

  const headers = { Authorization: `Bearer ${token}` };
  
  // Get weeks for the selected month
  const weeksInMonth = getWeeksInMonth(selectedYear, selectedMonth);

  useEffect(() => {
    fetchData();
  }, [selectedYear, selectedMonth, viewMode, selectedWeek]);

  const fetchData = async () => {
    setLoading(true);
    try {
      let statsUrl = `${API}/stats/team?year=${selectedYear}&month=${selectedMonth}`;
      if (viewMode === 'weekly' && selectedWeek) {
        statsUrl += `&week=${selectedWeek}`;
      }
      
      const [statsRes, peopleRes] = await Promise.all([
        axios.get(statsUrl, { headers }),
        axios.get(`${API}/salespeople`, { headers }),
      ]);
      setTeamStats(statsRes.data);
      setSalespeople(peopleRes.data);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  // Get trophy icon for ranking
  const getTrophyIcon = (rank) => {
    if (rank === 1) return <Trophy className="w-4 h-4 text-yellow-400" />;
    if (rank === 2) return <Medal className="w-4 h-4 text-slate-300" />;
    if (rank === 3) return <Award className="w-4 h-4 text-amber-600" />;
    return null;
  };

  // Get department color
  const getDeptColor = (dept) => {
    return DEPARTMENTS[dept]?.color || "#6366F1";
  };

  const getDeptBgColor = (dept) => {
    return DEPARTMENTS[dept]?.bgColor || "bg-indigo-600";
  };

  const metricLabels = {
    // Sales
    team_generated: "Team Generated",
    self_generated: "Self Generated",
    demos_held: "Demos Held",
    upgrades_closed: "Upgrades Closed",
    new_deals_closed: "New Deals Closed",
    // CSM
    renewals: "Renewals",
    upsells: "Upsells",
    customer_calls: "Customer Calls",
    nps_score: "NPS Score",
    churn_prevention: "Churn Saved",
    // Dev
    tickets_closed: "Tickets Closed",
    features_shipped: "Features Shipped",
    bugs_fixed: "Bugs Fixed",
    code_reviews: "Code Reviews",
    deployments: "Deployments",
    // Support
    tickets_resolved: "Tickets Resolved",
    first_response_time: "Response Time",
    customer_satisfaction: "CSAT Score",
    escalations_handled: "Escalations",
    knowledge_articles: "KB Articles",
    // Marketing
    leads_generated: "Leads Generated",
    campaigns_launched: "Campaigns",
    content_published: "Content Published",
    social_engagement: "Social Engagement",
    email_opens: "Email Opens",
  };

  const chartColors = ["#6366F1", "#10B981", "#8B5CF6", "#F59E0B", "#EC4899"];

  // Handle column sorting
  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === "desc" ? "asc" : "desc");
    } else {
      setSortColumn(column);
      setSortDirection("desc");
    }
  };

  // Get sort icon for column header
  const getSortIcon = (column) => {
    if (sortColumn !== column) {
      return <ArrowUpDown size={14} className="ml-1 opacity-40" />;
    }
    return sortDirection === "desc" 
      ? <ArrowDown size={14} className="ml-1 text-amber-400" />
      : <ArrowUp size={14} className="ml-1 text-amber-400" />;
  };

  // Sorted and filtered salesperson data
  const sortedSalespeople = useMemo(() => {
    if (!teamStats?.by_salesperson) return [];
    
    // Revenue fields should not be summed with activity counts
    const revenueFields = ['deals_revenue', 'renewals_revenue', 'upsells_revenue'];
    
    // Add department info from salespeople and calculate totals (excluding revenue)
    let data = teamStats.by_salesperson.map(p => {
      const personInfo = salespeople.find(sp => sp.id === p.salesperson.id);
      // Only sum non-revenue metrics for the total
      const total = Object.entries(p.totals).reduce((sum, [key, v]) => {
        if (revenueFields.includes(key)) return sum; // Skip revenue fields
        return sum + (v || 0);
      }, 0);
      return { ...p, department: personInfo?.department || "sales", total };
    });
    
    // Filter by department if not "all"
    if (selectedDepartment !== "all") {
      data = data.filter(p => p.department === selectedDepartment);
    }
    
    // First, sort by total to determine original ranks
    const rankedData = [...data].sort((a, b) => b.total - a.total);
    const rankMap = {};
    rankedData.forEach((p, idx) => {
      rankMap[p.salesperson.id] = idx + 1;
    });
    
    // Add original rank to each person
    data = data.map(p => ({ ...p, originalRank: rankMap[p.salesperson.id] }));
    
    // Then sort by the selected column
    return data.sort((a, b) => {
      let aValue, bValue;
      
      if (sortColumn === "total") {
        aValue = a.total;
        bValue = b.total;
      } else if (sortColumn === "name") {
        aValue = a.salesperson.name?.toLowerCase() || "";
        bValue = b.salesperson.name?.toLowerCase() || "";
        return sortDirection === "desc" 
          ? bValue.localeCompare(aValue)
          : aValue.localeCompare(bValue);
      } else {
        aValue = a.totals[sortColumn] || 0;
        bValue = b.totals[sortColumn] || 0;
      }
      
      return sortDirection === "desc" ? bValue - aValue : aValue - bValue;
    });
  }, [teamStats, sortColumn, sortDirection, salespeople, selectedDepartment]);

  // Calculate revenue data separately from activity metrics - ALL TEAMS
  const revenueData = useMemo(() => {
    if (!teamStats?.by_salesperson) return { total: 0, byPerson: [] };
    
    const byPerson = teamStats.by_salesperson.map(p => {
      const personInfo = salespeople.find(sp => sp.id === p.salesperson.id);
      
      // Include ALL revenue types from ANY department
      const dealsRevenue = p.totals.deals_revenue || 0;      // Sales
      const renewalsRevenue = p.totals.renewals_revenue || 0; // CSM
      const upsellsRevenue = p.totals.upsells_revenue || 0;   // CSM
      const totalRevenue = dealsRevenue + renewalsRevenue + upsellsRevenue;
      
      return {
        id: p.salesperson.id,
        name: p.salesperson.name,
        department: personInfo?.department || "sales",
        dealsRevenue,
        renewalsRevenue,
        upsellsRevenue,
        totalRevenue
      };
    }).filter(p => p.totalRevenue > 0) // Only show people with revenue
      .sort((a, b) => b.totalRevenue - a.totalRevenue);
    
    const total = byPerson.reduce((sum, p) => sum + p.totalRevenue, 0);
    
    // Calculate revenue by department
    const byDepartment = {};
    byPerson.forEach(p => {
      if (!byDepartment[p.department]) {
        byDepartment[p.department] = { total: 0, count: 0 };
      }
      byDepartment[p.department].total += p.totalRevenue;
      byDepartment[p.department].count += 1;
    });
    
    return { total, byPerson, byDepartment };
  }, [teamStats, salespeople]);

  // Department-specific metrics for table columns
  const DEPT_TABLE_METRICS = {
    all: ["team_generated", "self_generated", "demos_held", "upgrades_closed", "new_deals_closed"],
    sales: ["team_generated", "self_generated", "demos_held", "upgrades_closed", "new_deals_closed"],
    csm: ["renewals", "upsells", "customer_calls", "nps_score", "churn_prevention"],
    dev: ["tickets_closed", "features_shipped", "bugs_fixed", "code_reviews", "deployments"],
    support: ["tickets_resolved", "escalations_handled", "customer_satisfaction", "knowledge_articles"],
    marketing: ["leads_generated", "campaigns_launched", "content_published", "social_engagement"],
  };

  // Short labels for table headers
  const shortMetricLabels = {
    team_generated: "Team Gen",
    self_generated: "Self Gen",
    demos_held: "Demos",
    upgrades_closed: "Upgrades",
    new_deals_closed: "Deals",
    renewals: "Renewals",
    upsells: "Upsells",
    customer_calls: "Calls",
    nps_score: "NPS",
    churn_prevention: "Saved",
    tickets_closed: "Tickets",
    features_shipped: "Features",
    bugs_fixed: "Bugs",
    code_reviews: "Reviews",
    deployments: "Deploys",
    tickets_resolved: "Resolved",
    escalations_handled: "Escalations",
    customer_satisfaction: "CSAT",
    knowledge_articles: "KB Arts",
    leads_generated: "Leads",
    campaigns_launched: "Campaigns",
    content_published: "Content",
    social_engagement: "Social",
  };

  // Get table metrics based on selected department OR person's department for "all" view
  const getTableMetricsForPerson = (personDept) => {
    if (selectedDepartment !== "all") {
      return DEPT_TABLE_METRICS[selectedDepartment] || DEPT_TABLE_METRICS.sales;
    }
    // When viewing "all", use the person's actual department metrics
    return DEPT_TABLE_METRICS[personDept] || DEPT_TABLE_METRICS.sales;
  };

  // Current table metrics (for headers when a specific department is selected)
  const currentTableMetrics = DEPT_TABLE_METRICS[selectedDepartment] || DEPT_TABLE_METRICS.sales;

  // Department-specific metrics for charts
  const DEPT_METRICS = {
    all: ["team_generated", "self_generated", "demos_held", "upgrades_closed", "new_deals_closed"],
    sales: ["team_generated", "self_generated", "demos_held", "upgrades_closed", "new_deals_closed"],
    csm: ["renewals", "upsells", "customer_calls", "nps_score", "churn_prevention"],
    dev: ["tickets_closed", "features_shipped", "bugs_fixed", "code_reviews", "deployments"],
    support: ["tickets_resolved", "first_response_time", "customer_satisfaction", "escalations_handled", "knowledge_articles"],
    marketing: ["leads_generated", "campaigns_launched", "content_published", "social_engagement", "email_opens"],
  };

  const currentDeptMetrics = DEPT_METRICS[selectedDepartment] || DEPT_METRICS.sales;

  const chartData = useMemo(() => {
    if (!teamStats) return [];
    return currentDeptMetrics
      .map((key, index) => ({
        name: metricLabels[key] || key,
        value: teamStats.team_totals[key] || 0,
        fill: chartColors[index % chartColors.length],
      }))
      .filter(item => item.value > 0 || selectedDepartment !== "all");
  }, [teamStats, selectedDepartment, currentDeptMetrics]);

  const totalActivities = useMemo(() => {
    if (!teamStats) return 0;
    return currentDeptMetrics.reduce((sum, key) => sum + (teamStats.team_totals[key] || 0), 0);
  }, [teamStats, currentDeptMetrics]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  return (
    <div className="animate-fade-in" data-testid="dashboard">
      {/* Compact Header */}
      <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
        <div>
          <h1 className="text-lg font-bold text-slate-100" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Team Dashboard
          </h1>
          <p className="text-xs text-slate-400">
            {viewMode === 'weekly' && selectedWeek ? `Week ending ${new Date(selectedWeek).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}` : `${MONTHS[selectedMonth - 1]} ${selectedYear}`}
          </p>
        </div>
        
        <div className="flex flex-wrap gap-2">
          {/* View Mode Toggle */}
          <div className="flex rounded-lg overflow-hidden border border-slate-700 h-8">
            <button
              onClick={() => setViewMode('weekly')}
              className={`px-3 text-xs font-medium transition-colors ${
                viewMode === 'weekly' 
                  ? 'bg-indigo-600 text-white' 
                  : 'bg-slate-800 text-slate-400 hover:text-slate-200'
              }`}
            >
              Weekly
            </button>
            <button
              onClick={() => setViewMode('monthly')}
              className={`px-3 text-xs font-medium transition-colors ${
                viewMode === 'monthly' 
                  ? 'bg-indigo-600 text-white' 
                  : 'bg-slate-800 text-slate-400 hover:text-slate-200'
              }`}
            >
              Monthly
            </button>
          </div>
          
          {/* Department Filter */}
          <Select value={selectedDepartment} onValueChange={setSelectedDepartment}>
            <SelectTrigger className="w-[100px] h-8 text-xs bg-slate-800 border-slate-700 text-slate-200">
              <Building2 size={12} className="mr-1" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-slate-800 border-slate-700">
              <SelectItem value="all" className="text-slate-200 text-xs">Teams</SelectItem>
              {Object.entries(DEPARTMENTS).map(([key, dept]) => (
                <SelectItem key={key} value={key} className="text-slate-200 text-xs">
                  {dept.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={selectedMonth.toString()} onValueChange={(v) => setSelectedMonth(Number(v))}>
            <SelectTrigger className="w-[100px] h-8 text-xs bg-slate-800 border-slate-700 text-slate-200" data-testid="month-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-slate-800 border-slate-700">
              {MONTHS.map((month, index) => (
                <SelectItem key={month} value={(index + 1).toString()} className="text-slate-200 text-xs">
                  {month}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={selectedYear.toString()} onValueChange={(v) => setSelectedYear(Number(v))}>
            <SelectTrigger className="w-[80px] h-8 text-xs bg-slate-800 border-slate-700 text-slate-200" data-testid="year-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-slate-800 border-slate-700">
              {[2024, 2025, 2026, 2027].map((year) => (
                <SelectItem key={year} value={year.toString()} className="text-slate-200 text-xs">
                  {year}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {/* Week Selector - only shown in weekly mode */}
          {viewMode === 'weekly' && weeksInMonth.length > 0 && (
            <Select value={selectedWeek} onValueChange={setSelectedWeek}>
              <SelectTrigger className="w-[150px] h-8 text-xs bg-slate-800 border-slate-700 text-slate-200">
                <CalendarDays size={12} className="mr-1" />
                <SelectValue placeholder="Select week" />
              </SelectTrigger>
              <SelectContent className="bg-slate-800 border-slate-700">
                {weeksInMonth.map((week) => (
                  <SelectItem key={week.value} value={week.value} className="text-slate-200 text-xs">
                    {week.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>
      </div>

      {/* Top 3 Performers - Compact */}
      <div className="grid grid-cols-3 gap-2 mb-4">
        {sortedSalespeople.slice(0, 3).map((person, idx) => {
          // Use the already-calculated total from sortedSalespeople (excludes revenue)
          const total = person.total;
          return (
            <div 
              key={person.salesperson.id}
              className={`p-2 rounded-lg border ${
                idx === 0 ? "bg-yellow-500/10 border-yellow-500/30" :
                idx === 1 ? "bg-slate-400/10 border-slate-400/30" :
                "bg-amber-600/10 border-amber-600/30"
              }`}
            >
              <div className="flex items-center gap-1.5 mb-0.5">
                {getTrophyIcon(idx + 1)}
                <span className="text-xs font-medium text-slate-300 truncate">{person.salesperson.name}</span>
              </div>
              <div className={`text-lg font-bold ${
                idx === 0 ? "text-yellow-400" : idx === 1 ? "text-slate-300" : "text-amber-500"
              }`}>
                {total.toFixed(0)}
              </div>
              <div className="text-[10px] text-slate-500">{DEPARTMENTS[person.department]?.label || "Sales"}</div>
            </div>
          );
        })}
      </div>

      {/* Metric Cards - Larger with clear labels, sorted by value */}
      <div className="bg-slate-800/50 rounded-lg p-4 border border-slate-700/50 mb-4">
        <h3 className="text-sm font-semibold text-slate-300 mb-3">
          {DEPARTMENTS[selectedDepartment]?.label || "Teams"} Metrics - {MONTHS[selectedMonth - 1]}
        </h3>
        <div className="space-y-2">
          {currentDeptMetrics
            .map((key, idx) => ({
              key,
              label: metricLabels[key] || key,
              value: teamStats ? (teamStats.team_totals[key] || 0) : 0,
              color: chartColors[idx % chartColors.length]
            }))
            .sort((a, b) => b.value - a.value)
            .map((metric, idx) => (
              <div 
                key={metric.key} 
                className="flex items-center gap-3 p-3 rounded-lg border border-slate-700/30 bg-slate-900/30"
                data-testid={`metric-${metric.key}`}
              >
                <div 
                  className="w-10 h-10 rounded-lg flex items-center justify-center text-white font-bold text-sm flex-shrink-0"
                  style={{ backgroundColor: metric.color }}
                >
                  #{idx + 1}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-slate-200">{metric.label}</p>
                  <div className="w-full bg-slate-700/50 rounded-full h-2 mt-1">
                    <div 
                      className="h-2 rounded-full transition-all"
                      style={{ 
                        width: `${Math.min(100, (metric.value / Math.max(...currentDeptMetrics.map(k => teamStats?.team_totals[k] || 1))) * 100)}%`,
                        backgroundColor: metric.color 
                      }}
                    />
                  </div>
                </div>
                <div className="text-right flex-shrink-0">
                  <p className="text-xl font-bold" style={{ color: metric.color }}>
                    {metric.value.toFixed(0)}
                  </p>
                  <p className="text-[10px] text-slate-500">total</p>
                </div>
              </div>
            ))}
        </div>
      </div>

      {/* Charts and Summary - Side by Side Compact */}
      <div className="grid grid-cols-2 gap-3 mb-4">
        {/* Bar Chart */}
        <div className="bg-slate-800/50 rounded-lg p-3 border border-slate-700/50">
          <h3 className="text-xs font-semibold text-slate-300 mb-2">
            Team Totals - {MONTHS[selectedMonth - 1]}
          </h3>
          <div className="h-[140px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData} layout="vertical" margin={{ left: 0, right: 10 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#334155" horizontal={true} vertical={false} />
                <XAxis type="number" tick={{ fill: '#94a3b8', fontSize: 10 }} />
                <YAxis 
                  type="category" 
                  dataKey="name" 
                  tick={{ fill: '#94a3b8', fontSize: 10 }} 
                  width={70}
                />
                <Tooltip
                  contentStyle={{
                    background: '#1e293b',
                    border: '1px solid #334155',
                    borderRadius: '8px',
                    color: '#f1f5f9',
                    fontSize: '11px',
                  }}
                  cursor={false}
                  labelStyle={{ color: '#f1f5f9' }}
                  itemStyle={{ color: '#f1f5f9' }}
                />
                <Bar dataKey="value" radius={[0, 4, 4, 0]}>
                  {chartData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.fill} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Summary Stats - Compact */}
        <div className="bg-slate-800/50 rounded-lg p-3 border border-slate-700/50">
          <h3 className="text-xs font-semibold text-slate-300 mb-2">Monthly Summary</h3>
          <div className="space-y-2">
            <div className="flex items-center justify-between p-2 bg-indigo-500/10 border border-indigo-500/20 rounded-lg">
              <div className="flex items-center gap-2">
                <div className="w-7 h-7 bg-indigo-600 rounded flex items-center justify-center">
                  <TrendingUp className="text-white" size={14} />
                </div>
                <div>
                  <p className="text-[10px] text-slate-400">Total Activities</p>
                  <p className="text-lg font-bold text-slate-100">{totalActivities.toFixed(0)}</p>
                </div>
              </div>
            </div>
            
            <div className="flex items-center justify-between p-2 bg-emerald-500/10 border border-emerald-500/20 rounded-lg">
              <div className="flex items-center gap-2">
                <div className="w-7 h-7 bg-emerald-600 rounded flex items-center justify-center">
                  <Target className="text-white" size={14} />
                </div>
                <div>
                  <p className="text-[10px] text-slate-400">Deals Closed</p>
                  <p className="text-lg font-bold text-slate-100">
                    {teamStats ? teamStats.team_totals.new_deals_closed.toFixed(0) : 0}
                  </p>
                </div>
              </div>
            </div>
            
            <div className="flex items-center justify-between p-2 bg-amber-500/10 border border-amber-500/20 rounded-lg">
              <div className="flex items-center gap-2">
                <div className="w-7 h-7 bg-amber-600 rounded flex items-center justify-center">
                  <Users className="text-white" size={14} />
                </div>
                <div>
                  <p className="text-[10px] text-slate-400">Active Team</p>
                  <p className="text-lg font-bold text-slate-100">{sortedSalespeople.length}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Monthly Revenue Section - Separate from KPIs - ALL TEAMS */}
      {revenueData.total > 0 && (
        <div className="bg-gradient-to-br from-emerald-900/30 to-slate-800/50 rounded-lg p-3 border border-emerald-700/30 mt-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-emerald-400 flex items-center gap-2">
              <DollarSign size={16} className="text-emerald-500" />
              Monthly Revenue (All Teams)
            </h3>
            <div className="text-right">
              <p className="text-2xl font-bold text-emerald-400">
                ${revenueData.total.toLocaleString()}
              </p>
              <p className="text-[10px] text-slate-400">
                {MONTHS[selectedMonth - 1]} {selectedYear}
              </p>
            </div>
          </div>
          
          {/* Revenue by Department Summary */}
          {Object.keys(revenueData.byDepartment || {}).length > 1 && (
            <div className="flex flex-wrap gap-2 mb-3 pb-3 border-b border-slate-700/50">
              {Object.entries(revenueData.byDepartment).map(([dept, data]) => (
                <div 
                  key={dept}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium ${
                    dept === 'sales' ? 'bg-indigo-500/20 text-indigo-300 border border-indigo-500/30' :
                    dept === 'csm' ? 'bg-cyan-500/20 text-cyan-300 border border-cyan-500/30' :
                    dept === 'dev' ? 'bg-violet-500/20 text-violet-300 border border-violet-500/30' :
                    dept === 'support' ? 'bg-amber-500/20 text-amber-300 border border-amber-500/30' :
                    dept === 'marketing' ? 'bg-pink-500/20 text-pink-300 border border-pink-500/30' :
                    'bg-slate-500/20 text-slate-300 border border-slate-500/30'
                  }`}
                >
                  <span className="capitalize">{dept}</span>: ${data.total.toLocaleString()} ({data.count})
                </div>
              ))}
            </div>
          )}
          
          {/* Revenue by Salesperson */}
          <div className="space-y-2">
            <p className="text-[10px] text-slate-500 uppercase tracking-wider">Revenue by Team Member</p>
            <div className="grid gap-2">
              {revenueData.byPerson.slice(0, 10).map((person, idx) => (
                <div 
                  key={person.id}
                  className="flex items-center justify-between p-2 bg-slate-800/60 rounded-lg border border-slate-700/50"
                >
                  <div className="flex items-center gap-2">
                    <span className={`text-xs font-bold w-5 h-5 flex items-center justify-center rounded ${
                      idx === 0 ? 'bg-emerald-600 text-white' : 
                      idx === 1 ? 'bg-emerald-700 text-white' : 
                      idx === 2 ? 'bg-emerald-800 text-white' : 
                      'bg-slate-700 text-slate-400'
                    }`}>
                      {idx + 1}
                    </span>
                    <div>
                      <p className="text-sm font-medium text-slate-200">{person.name}</p>
                      <p className={`text-[10px] capitalize ${
                        person.department === 'sales' ? 'text-indigo-400' :
                        person.department === 'csm' ? 'text-cyan-400' :
                        person.department === 'dev' ? 'text-violet-400' :
                        person.department === 'support' ? 'text-amber-400' :
                        person.department === 'marketing' ? 'text-pink-400' :
                        'text-slate-500'
                      }`}>{person.department}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-bold text-emerald-400">
                      ${person.totalRevenue.toLocaleString()}
                    </p>
                    {(person.dealsRevenue > 0 || person.renewalsRevenue > 0 || person.upsellsRevenue > 0) && (
                      <div className="flex gap-2 text-[9px] text-slate-500">
                        {person.dealsRevenue > 0 && <span>Deals: ${person.dealsRevenue.toLocaleString()}</span>}
                        {person.renewalsRevenue > 0 && <span>Renew: ${person.renewalsRevenue.toLocaleString()}</span>}
                        {person.upsellsRevenue > 0 && <span>Upsell: ${person.upsellsRevenue.toLocaleString()}</span>}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
            {revenueData.byPerson.length > 10 && (
              <p className="text-[10px] text-slate-500 text-center mt-2">
                + {revenueData.byPerson.length - 10} more team members with revenue
              </p>
            )}
          </div>
        </div>
      )}

      {/* Team Performance Table - Compact */}
      <div className="bg-slate-800/50 rounded-lg p-2 sm:p-3 border border-slate-700/50">
        <h3 className="text-xs font-semibold text-slate-300 mb-2">Team Performance</h3>
        
        <div className="overflow-x-auto -mx-2 sm:-mx-3 px-2 sm:px-3">
          <table className="min-w-[600px] w-full text-[11px] sm:text-xs" data-testid="team-table">
            <thead>
              <tr className="border-b border-slate-700">
                <th 
                  className="text-left py-2 px-2 text-slate-400 font-medium cursor-pointer hover:text-slate-200 sticky left-0 bg-slate-800/90 backdrop-blur-sm z-10"
                  onClick={() => handleSort("name")}
                  data-testid="sort-name"
                >
                  <div className="flex items-center">
                    Name {getSortIcon("name")}
                  </div>
                </th>
                {currentTableMetrics.slice(0, 5).map((metricKey) => (
                  <th 
                    key={metricKey}
                    className="text-right py-2 px-2 sm:px-3 text-slate-400 font-medium cursor-pointer hover:text-slate-200 whitespace-nowrap"
                    onClick={() => handleSort(metricKey)}
                  >
                    <div className="flex items-center justify-end">
                      {shortMetricLabels[metricKey] || metricKey} {getSortIcon(metricKey)}
                    </div>
                  </th>
                ))}
                <th 
                  className="text-right py-2 px-2 sm:px-3 text-slate-400 font-medium cursor-pointer hover:text-slate-200"
                  onClick={() => handleSort("total")}
                  data-testid="sort-total"
                >
                  <div className="flex items-center justify-end">
                    Total {getSortIcon("total")}
                  </div>
                </th>
              </tr>
            </thead>
            <tbody>
              {sortedSalespeople.map((person, index) => {
                // Get metrics for this person's department
                const personMetrics = selectedDepartment === "all" 
                  ? DEPT_TABLE_METRICS[person.department] || DEPT_TABLE_METRICS.sales
                  : currentTableMetrics;
                
                return (
                  <tr 
                    key={person.salesperson.id} 
                    className="border-b border-slate-700/50 hover:bg-slate-700/20 cursor-pointer" 
                    data-testid={`salesperson-row-${index}`}
                    onClick={() => navigate(`/scorecards/${person.salesperson.id}`)}
                  >
                    <td className="py-2 px-2 sticky left-0 bg-slate-800/90 backdrop-blur-sm">
                      <div className="flex items-center gap-1.5">
                        {person.originalRank <= 3 ? (
                          getTrophyIcon(person.originalRank)
                        ) : (
                          <span className="text-[10px] font-bold min-w-[16px] text-slate-500">
                            #{person.originalRank}
                          </span>
                        )}
                        <div className={`w-5 h-5 sm:w-6 sm:h-6 rounded flex items-center justify-center text-white text-[9px] sm:text-[10px] font-bold flex-shrink-0 ${getDeptBgColor(person.department)}`}>
                          {person.salesperson.name?.charAt(0)}
                        </div>
                        <span className="text-slate-200 truncate max-w-[80px] sm:max-w-[120px]">{person.salesperson.name}</span>
                      </div>
                    </td>
                    {currentTableMetrics.slice(0, 5).map((metricKey, metricIdx) => {
                      // When viewing "all", show person's actual metrics mapped to table position
                      const value = selectedDepartment === "all" && person.department !== "sales"
                        ? (person.totals[personMetrics[metricIdx]] || 0)
                        : (person.totals[metricKey] || 0);
                      const isDeals = metricKey === "new_deals_closed" || metricKey === "renewals" || metricKey === "tickets_closed" || metricKey === "leads_generated";
                      return (
                        <td 
                          key={metricKey} 
                          className={`text-right py-2 px-2 sm:px-3 ${isDeals ? 'font-semibold text-emerald-400' : 'text-slate-300'}`}
                        >
                          {value.toFixed(0)}
                        </td>
                      );
                    })}
                    <td className="text-right py-2 px-2 sm:px-3 font-bold" style={{ color: getDeptColor(person.department) }}>{person.total.toFixed(0)}</td>
                  </tr>
                );
              })}
              {(!sortedSalespeople || sortedSalespeople.length === 0) && (
                <tr>
                  <td colSpan={7} className="text-center py-4 text-slate-500 text-xs">
                    No data available for this period.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
